import { Helmet } from 'react-helmet-async';
import { DEFAULT_OG_IMAGE, LOCALE_OG, SITE_NAME, SITE_ORIGIN } from '@/seo/siteConfig';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  ogImageAlt?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: 'website' | 'product' | 'article';
  twitterCard?: 'summary' | 'summary_large_image';
  twitterSite?: string; // e.g., "@systemique_nl"
  twitterCreator?: string; // e.g., "@systemique_nl"
  twitterImageAlt?: string;
  noindex?: boolean;
  author?: string;
  geoRegion?: string;
  geoPlacename?: string;
  geoPosition?: string; // Format: "latitude;longitude"
  // Article specific
  articleAuthor?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleSection?: string;
  articleTags?: string[];
  /** Extra JSON-LD documenten (`@context` + `@type` per object). */
  jsonLd?: Record<string, unknown>[];
  /** Expliciete robots-directive, bv. `index, follow` of `noindex, nofollow`. */
  robotsDirective?: string;
  // Schema.org structured data
  schemaOrg?: {
    organization?: object;
    breadcrumbList?: object;
    article?: object;
    localBusiness?: object;
  };
  // Performance
  preconnectDomains?: string[];
  dnsPrefetchDomains?: string[];
  // Theme
  themeColor?: string;
  // Hreflang
  hreflang?: { locale: string; href: string }[];
  // Mobile
  mobileWebAppCapable?: boolean;
  appleMobileWebAppCapable?: boolean;
  appleMobileWebAppStatusBarStyle?: 'default' | 'black' | 'black-translucent';
}

export const SEO = ({
  title,
  description,
  keywords,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogImageWidth,
  ogImageHeight,
  ogImageAlt,
  ogTitle,
  ogDescription,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  twitterSite,
  twitterCreator,
  twitterImageAlt,
  noindex = false,
  robotsDirective,
  jsonLd,
  author,
  geoRegion,
  geoPlacename,
  geoPosition,
  articleAuthor,
  articlePublishedTime,
  articleModifiedTime,
  articleSection,
  articleTags,
  schemaOrg,
  preconnectDomains = ['https://fonts.googleapis.com', 'https://fonts.gstatic.com'],
  dnsPrefetchDomains = ['https://fonts.googleapis.com', 'https://fonts.gstatic.com'],
  themeColor = '#854C73',
  hreflang,
  mobileWebAppCapable = false,
  appleMobileWebAppCapable = false,
  appleMobileWebAppStatusBarStyle = 'black-translucent',
}: SEOProps) => {
  const pathFallback = typeof window !== 'undefined' ? window.location.pathname : '/';
  const canonicalUrl = canonical ?? `${SITE_ORIGIN}${pathFallback === '//' ? '/' : pathFallback}`;
  const stripLegacyBrand = (value: string) =>
    value.replace(/\s*[\|–-]\s*Systemique\s*$/i, '').trim();

  const buildFullTitle = (raw: string) => {
    const cleaned = stripLegacyBrand(raw);
    const hasBrand =
      cleaned === SITE_NAME ||
      cleaned.toLowerCase().includes(SITE_NAME.toLowerCase());

    return hasBrand ? cleaned : `${cleaned} | ${SITE_NAME}`;
  };

  const fullTitle = buildFullTitle(title);
  const fullOgTitle = buildFullTitle(ogTitle ?? title);
  const finalOGImage = ogImage || DEFAULT_OG_IMAGE;
  const robotsContent = noindex
    ? 'noindex, nofollow'
    : (robotsDirective ?? 'index, follow');

  // Default geo position for Castricum if geoPlacename is Castricum
  const finalGeoPosition = geoPosition || (geoPlacename === 'Castricum' ? '52.5483;4.6694' : undefined);

  return (
    <Helmet
      htmlAttributes={{
        lang: 'nl',
      }}
    >
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {author && <meta name="author" content={author} />}
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content={robotsContent} />
      
      {/* Theme color for mobile browsers */}
      <meta name="theme-color" content={themeColor} />
      <meta name="msapplication-TileColor" content={themeColor} />
      
      {/* Format detection - prevent auto-linking of phone numbers */}
      <meta name="format-detection" content="telephone=no" />
      
      {/* Geo tags for local SEO */}
      {geoRegion && <meta name="geo.region" content={geoRegion} />}
      {geoPlacename && <meta name="geo.placename" content={geoPlacename} />}
      {finalGeoPosition && (
        <>
          <meta name="geo.position" content={finalGeoPosition} />
          <meta name="ICBM" content={finalGeoPosition.replace(';', ', ')} />
        </>
      )}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullOgTitle} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:image" content={finalOGImage} />
      {ogImageWidth && <meta property="og:image:width" content={ogImageWidth.toString()} />}
      {ogImageHeight && <meta property="og:image:height" content={ogImageHeight.toString()} />}
      {ogImageAlt && <meta property="og:image:alt" content={ogImageAlt} />}
      <meta property="og:site_name" content="Organisatieopstellingen" />
      <meta property="og:locale" content={LOCALE_OG} />
      <meta property="og:locale:alternate" content="nl_BE" />

      {/* Article specific meta tags */}
      {ogType === 'article' && (
        <>
          {articleAuthor && <meta property="article:author" content={articleAuthor} />}
          {articlePublishedTime && <meta property="article:published_time" content={articlePublishedTime} />}
          {articleModifiedTime && <meta property="article:modified_time" content={articleModifiedTime} />}
          {articleSection && <meta property="article:section" content={articleSection} />}
          {articleTags && articleTags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter Card: name= (niet property=) volgens X-specificatie */}
      <meta name="twitter:card" content={twitterCard} />
      {twitterSite && <meta name="twitter:site" content={twitterSite} />}
      {twitterCreator && <meta name="twitter:creator" content={twitterCreator} />}
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullOgTitle} />
      <meta name="twitter:description" content={ogDescription || description} />
      <meta name="twitter:image" content={finalOGImage} />
      {twitterImageAlt && <meta name="twitter:image:alt" content={twitterImageAlt} />}

      {/* Performance: Preconnect to external domains */}
      {preconnectDomains && preconnectDomains.map((domain, index) => (
        <link key={`preconnect-${index}`} rel="preconnect" href={domain} crossOrigin={domain.includes('fonts.gstatic.com') ? 'anonymous' : undefined} />
      ))}

      {/* Performance: DNS Prefetch */}
      {dnsPrefetchDomains && dnsPrefetchDomains.map((domain, index) => (
        <link key={`dns-prefetch-${index}`} rel="dns-prefetch" href={domain} />
      ))}

      {/* Hreflang tags */}
      {hreflang && hreflang.map((lang) => (
        <link key={lang.locale} rel="alternate" hreflang={lang.locale} href={lang.href} />
      ))}

      {/* Mobile Web App Meta Tags */}
      {mobileWebAppCapable && <meta name="mobile-web-app-capable" content="yes" />}
      {appleMobileWebAppCapable && <meta name="apple-mobile-web-app-capable" content="yes" />}
      {appleMobileWebAppCapable && (
        <meta name="apple-mobile-web-app-status-bar-style" content={appleMobileWebAppStatusBarStyle} />
      )}

      {/* Schema.org Structured Data - Organization */}
      {schemaOrg?.organization && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            ...schemaOrg.organization,
          })}
        </script>
      )}

      {/* Schema.org Structured Data - BreadcrumbList */}
      {schemaOrg?.breadcrumbList && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            ...schemaOrg.breadcrumbList,
          })}
        </script>
      )}

      {/* Schema.org Structured Data - Article */}
      {schemaOrg?.article && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            ...schemaOrg.article,
          })}
        </script>
      )}

      {/* Schema.org Structured Data - LocalBusiness */}
      {schemaOrg?.localBusiness && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'LocalBusiness',
            ...schemaOrg.localBusiness,
          })}
        </script>
      )}

      {jsonLd?.map((doc, index) => (
        <script key={`jsonld-${index}`} type="application/ld+json">
          {JSON.stringify(doc)}
        </script>
      ))}
    </Helmet>
  );
};

